import { cn } from '@/lib/cn';
import { humanise } from '@/lib/format';

/**
 * Status indicator.
 *
 * WCAG 2.1 AA (NFR-12) requires that status is not conveyed by colour alone, so every pill
 * carries its label as text and a shape marker. A red dot and a green dot are identical to
 * a user with deuteranopia; "Overdue" and "Paid" are not.
 */

type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const TONES: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
  info: 'bg-blue-50 text-blue-800 ring-blue-200',
  success: 'bg-green-50 text-green-800 ring-green-200',
  warning: 'bg-amber-50 text-amber-900 ring-amber-200',
  danger: 'bg-red-50 text-red-800 ring-red-200',
};

const DOTS: Record<Tone, string> = {
  neutral: 'bg-slate-400',
  info: 'bg-blue-500',
  success: 'bg-green-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
};

/** Documented status → tone map, shared by invoices, bills and events. */
const STATUS_TONES: Record<string, Tone> = {
  // Documents (FR-INV-009, FR-BILL-009)
  DRAFT: 'neutral',
  ISSUED: 'info',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  OVERDUE: 'danger',
  CONVERTED: 'info',
  CANCELLED: 'neutral',
  // Events (FR-EVENT-005)
  ENQUIRY: 'neutral',
  CONFIRMED: 'info',
  COMPLETED: 'success',
  // Discount requests (FR-DISC-003)
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  // Notifications (FR-NOTIF-005)
  QUEUED: 'neutral',
  SENT: 'success',
  FAILED: 'danger',
};

export interface StatusPillProps {
  status: string;
  className?: string;
  /** Override the tone where context differs from the default map. */
  tone?: Tone;
}

export function StatusPill({ status, className, tone }: StatusPillProps) {
  const resolved = tone ?? STATUS_TONES[status] ?? 'neutral';
  const label = humanise(status);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        TONES[resolved],
        // A cancelled document stays visible but reads as struck through (FR-LED-003).
        status === 'CANCELLED' && 'line-through decoration-slate-400',
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', DOTS[resolved])} aria-hidden="true" />
      {label}
    </span>
  );
}

export interface BadgeProps {
  children: React.ReactNode;
  tone?: Tone;
  className?: string;
}

export function Badge({ children, tone = 'neutral', className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
